import DOMPurify from 'dompurify';
import { Button, ModalDialog, ModalOverlay } from '@flos/react-ui';
import { useHippoText } from '../../../hooks/useHippoText';
import { useAddOrRemoveCoverage } from '../../../recoil/hooks/useAddOrRemoveCoverage';
import { useCalculateCoveragesPrice } from '../../../recoil/hooks/useCalculateCoveragesPrice';
import { useUpdateCoverableInformation } from '../../../recoil/hooks/useUpdateCoverableInformation';
import { useRecoilValue } from 'recoil';
import { allCoverablesOnsaAtom } from '../../../recoil/atoms/allCoverablesOnsaAtom';
import { getOnsaCurrentProductSelector } from '../../../recoil/selectors/getOnsaCurrentProductSelector';
import { getOnsaCoverable } from '../../../utils/coverablesUtil';
import type { AdditionalCoverageProps } from '../config/AdditionalCoveragesConfig';

const AddAdditionalCoverageConfirmationModal = ({
    coverage,
    isLoading,
    setIsLoading,
    showModal,
    setShowModal,
    closeModal
}: {
    coverage: AdditionalCoverageProps;
    isLoading: boolean;
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
    showModal: boolean;
    setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
    closeModal: () => void;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const { addOrRemoveCoverage } = useAddOrRemoveCoverage();
    const { calculateCoveragesPrice } = useCalculateCoveragesPrice();
    const { updateCoverableInformation } = useUpdateCoverableInformation();
    const { flavourName, fixedId } = useRecoilValue(getOnsaCurrentProductSelector);
    const coverables = useRecoilValue(allCoverablesOnsaAtom);
    const coverable = getOnsaCoverable(coverables, flavourName, fixedId);

    const handleAddCoverage = (): void => {
        setIsLoading(true);
        void addOrRemoveCoverage(coverage.coverageId, coverable.packageGroupName ?? 'Default')
            .then(calculateCoveragesPrice)
            .then((response) => {
                updateCoverableInformation({
                    coverages: response.coverages,
                    monthlyPrice: response.coverablePrice.monthlyPrice,
                    yearlyPrice: response.coverablePrice.yearlyPrice
                });
                setIsLoading(false);
                setShowModal(false);
            });
    };
    return (
        <ModalOverlay isOpen={showModal} onDismiss={closeModal}>
            <ModalDialog closeIconHandler={closeModal}>
                <div className="u-spacing-inset-l">
                    <div className="u-spacing-inset-l--stretch-max">
                        <h4 className="u-spacing-stack-sm">
                            {DOMPurify.sanitize(
                                getHippoText(coverage?.confirmationModalProps?.modalHeader ?? '')
                            )}
                        </h4>
                        <p
                            className="u-spacing-stack-sm"
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(
                                    getHippoText(
                                        coverage?.confirmationModalProps?.modalSubheader ?? ''
                                    )
                                )
                            }}
                        />
                    </div>
                    <Button onClick={handleAddCoverage} disabled={isLoading}>
                        {getHippoText('DSS-COMMON-CONTINUE')}
                    </Button>
                </div>
            </ModalDialog>
        </ModalOverlay>
    );
};

export default AddAdditionalCoverageConfirmationModal;
